import ClickAwayListener from "@mui/material/ClickAwayListener";
import isEqual from "lodash/isEqual";
import { useMemo, useState } from "react";

import { useCaptureElement } from "scmp-app/lib/hooks";

import {
  Display,
  DisplayContainer,
  StyledArrowIcon,
  StyledMenu,
  StyledMenuItem,
  StyledPaper,
} from "./styles";

type Options<T> = {
  count?: string;
  text: string;
  value: T;
};

type Props<T, TOption> = {
  className?: string;
  isChangedOptionColor?: boolean;
  onOptionSelect: (selectedOptionValue: TOption) => void;
  options: TOption[];
  placeholder?: string;
  selectedOptionValue?: T;
  withCount?: boolean;
};

export const DropdownMenu = <T, TOption extends Options<T> = Options<T>>({
  className,
  isChangedOptionColor = false,
  onOptionSelect,
  options,
  placeholder,
  selectedOptionValue,
  withCount = false,
}: Props<T, TOption>) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const { captureElement, element: anchorElement } = useCaptureElement<HTMLSpanElement>();

  const handleMenuOptionChanged = (option: TOption) => {
    setIsOpenMenu(false);
    onOptionSelect(option);
  };

  const selectedOptionText = useMemo(() => {
    if (selectedOptionValue === undefined) return;
    return options.find(option => isEqual(option.value, selectedOptionValue))?.text;
  }, [selectedOptionValue, options]);

  return (
    <>
      <DisplayContainer className={className}>
        <Display
          $isChangedOption={isChangedOptionColor}
          aria-expanded={isOpenMenu ? "true" : undefined}
          aria-haspopup="true"
          onClick={() => setIsOpenMenu(!isOpenMenu)}
          ref={captureElement}
        >
          {selectedOptionText && selectedOptionText !== "All" ? selectedOptionText : placeholder}
          <StyledArrowIcon $isExpand={isOpenMenu} />
        </Display>
      </DisplayContainer>
      {isOpenMenu && (
        <StyledMenu
          anchorEl={anchorElement}
          disablePortal
          modifiers={[
            {
              enabled: false,
              name: "flip",
            },
            { name: "offset", options: { offset: [0, -16] } },
          ]}
          open={isOpenMenu}
          placement="bottom-start"
        >
          <ClickAwayListener onClickAway={() => setIsOpenMenu(false)}>
            <StyledPaper>
              {options.map((option, index) => (
                <StyledMenuItem
                  $isSelected={isEqual(selectedOptionValue, option.value)}
                  key={index}
                  onClick={() => {
                    handleMenuOptionChanged(option);
                  }}
                >
                  {option.text}
                  {withCount && option.count && <span>{option.count}</span>}
                </StyledMenuItem>
              ))}
            </StyledPaper>
          </ClickAwayListener>
        </StyledMenu>
      )}
    </>
  );
};

DropdownMenu.displayName = "DropdownMenu";
